import { RefObject, useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * Tracks whether an element's content is visually clipped (by `text-overflow: ellipsis`
 * or plain `overflow: hidden`), so callers can show a tooltip with the full text only
 * when it isn't already fully visible.
 *
 * Re-measures whenever `content` changes and, where supported, whenever the element
 * itself resizes (column resize, window resize, responsive font-size changes).
 *
 * @param content the rendered text; changing it triggers a re-measure
 * @returns a ref to attach to the clipping element, and whether it is currently truncated
 */
export function useIsTruncated<T extends HTMLElement>(content?: string): [RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const measure = useCallback(() => {
    const element = ref.current;
    if (!element) {
      return;
    }
    // scrollWidth exceeds clientWidth exactly when the content overflows the box.
    setIsTruncated(element.scrollWidth > element.clientWidth);
  }, []);

  useLayoutEffect(() => {
    measure();

    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(() => measure());
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [content, measure]);

  return [ref, isTruncated];
}
